import { BookIcon, MailIcon, MessageCircleIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTitle } from "react-use";

import { trpc } from "@blis/trpc/react";

import { PermissionGate } from "@/components/PermissionGate";
import { SubHeader } from "@/components/SubHeader";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { Button, buttonVariants } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Keyboard } from "@/components/ui/keyboard";
import { Link, linkStyles } from "@/components/ui/link";
import { Text } from "@/components/ui/text";
import { useProductlaneChat } from "@/lib/productlane";

function Section({ children }: { children: ReactNode }) {
  return <div className="flex flex-col py-6 [&>h1]:pb-2">{children}</div>;
}

function Shortcut({ keys, children }: { keys: string[]; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <Keyboard keys={keys} />
      <Text>{children}</Text>
    </div>
  );
}

export function HelpPage() {
  useTitle("Help and Support");

  const { data } = trpc.orgs.list.useQuery({ orgType: "PRIMARY_LAB" });

  return (
    <>
      <SubHeader>
        <SubHeader.Left>
          <Breadcrumbs>
            <Breadcrumb>Support</Breadcrumb>
          </Breadcrumbs>
        </SubHeader.Left>
        <SubHeader.Right>
          <Text muted>
            Version: v{import.meta.env.VITE_BLIS_VERSION || "0.0.0"}
          </Text>
        </SubHeader.Right>
      </SubHeader>

      <div className="px-5 divide-y overflow-y-auto">
        <Section>
          <Heading level={2}>Lab Contact Information</Heading>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-1">
            {data?.map((lab) => (
              <div
                key={lab.id}
                className="flex flex-col gap-2 p-4 border rounded-md"
              >
                <Heading level={3}>{lab.name}</Heading>
                <div className="flex justify-between gap-4">
                  <div className="text-sm">
                    {lab.address}
                    <br />
                    {lab.city}, {lab.state} {lab.zip}
                  </div>
                  <div className="text-sm">
                    <p>{lab.phone}</p>
                    <p>{lab.email}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* <div className="flex items-center gap-2">
            <PhoneIcon className="size-4 text-muted-foreground" />
            <a
              href={`tel:${phone}`}
              className={linkStyles({ intent: "primary" })}
            >
              {phone}
            </a>
          </div>
          <div className="flex items-center gap-2">
            <MailIcon className="size-4 text-muted-foreground" />
            <a
              href={`mailto:${email}`}
              className={linkStyles({ intent: "primary" })}
            >
              {email}
            </a>
          </div> */}
        </Section>
        <Section>
          <Heading level={2}>bLIS support</Heading>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <MailIcon className="size-4 text-muted-foreground" />
              <a
                href="mailto:support@blis.app"
                className={linkStyles({ intent: "primary" })}
              >
                support@blis.app
              </a>
            </div>
            <div className="flex gap-2">
              <a
                href="https://docs.blis.app"
                target="_blank"
                rel="noreferrer"
                className={buttonVariants({ variant: "outline" })}
              >
                <BookIcon />
                bLIS documentation
              </a>
              <FeedbackButton />
            </div>
          </div>
        </Section>
        <PermissionGate requiredPermission="read:notifications">
          <Section>
            <Heading level={2}>Notification status</Heading>
            <Text muted className="pb-2">
              Check status of bLIS notifications
            </Text>
            <div>
              <Link
                href={{
                  to: "/help/notifications",
                }}
                className={buttonVariants({ variant: "outline" })}
              >
                Notification status
              </Link>
            </div>
          </Section>
        </PermissionGate>
        <Section>
          <Heading level={2}>Keyboard shortcuts</Heading>
          <Text muted className="pb-1">
            Global
          </Text>
          <div className="mb-8 flex flex-col items-start gap-2">
            <Shortcut keys={["Ctrl", "K"]}>Open command menu</Shortcut>
            <Shortcut keys={["C", "A"]}>Create accession</Shortcut>
            <Shortcut keys={["S", "O"]}>Sign out</Shortcut>
          </div>
          <Text muted className="pb-1">
            During order entry
          </Text>
          <div className="flex flex-col gap-2 items-start">
            <Shortcut keys={["T"]}>Enter today's date</Shortcut>
            <Shortcut keys={["Y"]}>Enter yesterday's date</Shortcut>
            <Shortcut keys={["C"]}>Copy collection date and time</Shortcut>
          </div>
        </Section>
      </div>
    </>
  );
}

function FeedbackButton() {
  const { buttonProps } = useProductlaneChat();

  return (
    <Button variant="outline" {...buttonProps}>
      <MessageCircleIcon />
      Submit a request
    </Button>
  );
}
